import type { TransactionType } from '@/lib/api/types';

/** Filters live in the URL, so a filtered list can be bookmarked, shared and survives reload. */
export interface TxFilters {
  q?: string;
  type?: TransactionType;
  account?: string;
  category?: string;
  from?: string;
  to?: string;
  uncategorized?: true;
}

const TYPES: readonly string[] = ['expense', 'income', 'transfer'];
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const ID = /^[0-9a-f-]{36}$/i;

const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
const matching = (v: unknown, re: RegExp) => (typeof v === 'string' && re.test(v) ? v : undefined);

/** Router `validateSearch`: keeps only well-formed values, so a hand-edited URL can never break the page. */
export function parseFilters(raw: Record<string, unknown>): TxFilters {
  const type = typeof raw.type === 'string' && TYPES.includes(raw.type) ? (raw.type as TransactionType) : undefined;
  const out: TxFilters = {};
  const set = <K extends keyof TxFilters>(k: K, v: TxFilters[K] | undefined) => {
    if (v !== undefined) out[k] = v;
  };
  set('q', text(raw.q));
  set('type', type);
  set('account', matching(raw.account, ID));
  set('category', matching(raw.category, ID));
  set('from', matching(raw.from, DAY));
  set('to', matching(raw.to, DAY));
  set('uncategorized', raw.uncategorized === true || raw.uncategorized === 'true' ? true : undefined);
  return out;
}

/** Filters except the search box (that has its own field), counted for the badge on the Filters button. */
export function activeFilterCount(f: TxFilters): number {
  return [f.type, f.account, f.category, f.from, f.to, f.uncategorized].filter((v) => v !== undefined).length;
}

export function hasAnyFilter(f: TxFilters): boolean {
  return activeFilterCount(f) > 0 || f.q !== undefined;
}

/** API query for the list; an uncategorized filter wins over a category choice. */
export function toApiQuery(f: TxFilters) {
  return {
    ...(f.q ? { q: f.q } : {}),
    ...(f.type ? { types: f.type } : {}),
    ...(f.account ? { account_ids: f.account } : {}),
    ...(f.uncategorized ? { uncategorized: true } : f.category ? { category_ids: f.category } : {}),
    ...(f.from ? { date_from: f.from } : {}),
    ...(f.to ? { date_to: f.to } : {}),
  };
}
